import { z } from 'zod';
import { sectionRepeatSchema, type SectionRepeat } from './document-v2.js';

/**
 * Model v3 — documents are trees of typed BLOCKS. A page is a list of
 * blocks; layout blocks nest children; any scalar prop can be a `$bind`
 * into the document's data tree (optionally formatted); pages and blocks
 * can repeat over arrays. Block types and their prop contracts live in the
 * blocks registry (@docgen/blocks) — this schema is deliberately
 * type-generic so the model (and its MCP surface, later) never trails the
 * palette.
 */

export const BIND_FORMATS = ['currency', 'number', 'date'] as const;
export type BindFormat = (typeof BIND_FORMATS)[number];

export const bindV3Schema = z.object({
  $bind: z.string().min(1),
  format: z.enum(BIND_FORMATS).optional(),
});
export type BindV3 = z.infer<typeof bindV3Schema>;

export function isBindV3(v: unknown): v is BindV3 {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Record<string, unknown>)['$bind'] === 'string'
  );
}

/** Token-constrained styling. Color values are theme tokens or CSS colors. */
export const styleOverridesSchema = z.object({
  pad: z.string().optional(),
  gap: z.number().optional(),
  /** Space above / below this block, in px — for spacing one block off another. */
  mt: z.number().optional(),
  mb: z.number().optional(),
  align: z.enum(['start', 'center', 'end', 'stretch']).optional(),
  justify: z.enum(['start', 'center', 'end', 'between']).optional(),
  bg: z.string().optional(),
  color: z.string().optional(),
  width: z.string().optional(),
  radius: z.number().optional(),
  grow: z.boolean().optional(),
  fontSize: z.string().optional(),
});
export type StyleOverrides = z.infer<typeof styleOverridesSchema>;

export interface BlockNode {
  id: string;
  type: string;
  props: Record<string, unknown>;
  style?: StyleOverrides | undefined;
  children?: BlockNode[] | undefined;
  repeat?: SectionRepeat | undefined;
}

export const blockNodeSchema: z.ZodType<BlockNode> = z.lazy(() =>
  z.object({
    id: z.string().min(1),
    type: z.string().min(1),
    props: z.record(z.string(), z.unknown()),
    style: styleOverridesSchema.optional(),
    children: z.array(blockNodeSchema).optional(),
    repeat: sectionRepeatSchema.optional(),
  }),
);

export const pageV3Schema = z.object({
  id: z.string().min(1),
  name: z.string().optional(),
  /** theme color token or CSS color for the page background */
  background: z.string().optional(),
  /** page padding override (CSS shorthand) */
  pad: z.string().optional(),
  repeat: sectionRepeatSchema.optional(),
  blocks: z.array(blockNodeSchema),
});
export type PageV3 = z.infer<typeof pageV3Schema>;

/**
 * Theme = named color tokens (injected as CSS vars) + free CSS for fonts
 * and page fundamentals. Block structural CSS lives with the blocks and
 * references the token vars.
 */
export const themeSchema = z.object({
  colors: z.record(z.string(), z.string()).default({}),
  baseCss: z.string().default(''),
});
export type Theme = z.infer<typeof themeSchema>;

export const documentV3Schema = z.object({
  version: z.literal(3),
  id: z.string().min(1),
  name: z.string().min(1),
  theme: themeSchema,
  data: z.record(z.string(), z.unknown()),
  pages: z.array(pageV3Schema),
  updatedAt: z.string().optional(),
});
export type DocumentV3 = z.infer<typeof documentV3Schema>;

/** A saved preset: a whole page or a fragment, insertable from the palette. */
export const templateDefSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  category: z.enum(['page', 'fragment']),
  background: z.string().optional(),
  pad: z.string().optional(),
  repeat: sectionRepeatSchema.optional(),
  blocks: z.array(blockNodeSchema),
});
export type TemplateDef = z.infer<typeof templateDefSchema>;
